import { useAtomValue } from 'jotai';
import { useState } from 'react';

import {
    adminUserAtom,
    selectedMembershipAtom,
} from '../../../entities/entityAtoms';
import { useDeleteMembershipIncomeAndRefetch } from '../../../member-profile/hooks/useDeleteMembershipIncome';
import { useUpsertMembershipIncomeAndRefetch } from '../../../member-profile/hooks/useUpsertMembershipIncome';
import type { MoneyMapperTakeHomePaySectionProps } from './MoneyMapperTakeHomePayTypes';
import { useMoneyMapperGuideMonthlyTakeHome } from './useMoneyMapperGuideMonthlyTakeHome';
import { useMoneyMapperIncomeDetailsModal } from './useMoneyMapperIncomeDetailsModal';
import { useMoneyMapperIncomePickerItems } from './useMoneyMapperIncomePickerItems';
import { useRefetchMembershipIncomesAndSyncMoneyMap } from './useRefetchMembershipIncomesAndSyncMoneyMap';

export const useMoneyMapperTakeHomePaySection = ({
    isSaving,
    moneyMap,
    persistMonthlyIncomeCentsToMoneyMap,
}: MoneyMapperTakeHomePaySectionProps) => {
    const membership = useAtomValue(selectedMembershipAtom);
    const adminUser = useAtomValue(adminUserAtom);

    const canMutateIncomes =
        adminUser.authorization.selectedMembership.general.canEdit;

    const { upsertMembershipIncomeAsync } =
        useUpsertMembershipIncomeAndRefetch(membership);
    const { deleteMembershipIncomeAsync } =
        useDeleteMembershipIncomeAndRefetch(membership);

    const pickers = useMoneyMapperIncomePickerItems(membership);

    const refetchMembershipAndSyncMoneyMap =
        useRefetchMembershipIncomesAndSyncMoneyMap({
            membership,
            persistMonthlyIncomeCentsToMoneyMap,
        });

    const incomes = membership?.membershipIncomes ?? [];

    const detailsModal = useMoneyMapperIncomeDetailsModal({
        membership,
        incomes,
        isSaving,
        canMutateIncomes,
        ownerPickerItems: pickers.ownerPickerItems,
        upsertMembershipIncomeAsync,
        deleteMembershipIncomeAsync,
        refetchMembershipAndSyncMoneyMap,
    });

    const guideTotal = useMoneyMapperGuideMonthlyTakeHome({
        moneyMap,
        membershipIncomes: incomes,
        persistMonthlyIncomeCentsToMoneyMap,
    });

    const [showNewIncomeRow, setShowNewIncomeRow] = useState(false);

    return {
        membership,
        canMutateIncomes,
        isSaving,
        pickers,
        incomes,
        mutations: {
            upsertMembershipIncomeAsync,
            refetchMembershipAndSyncMoneyMap,
        },
        detailsModal,
        newIncomeRow: {
            show: showNewIncomeRow,
            setShow: setShowNewIncomeRow,
        },
        guideTotal,
    };
};
